import { extractRpcErrorMessage, isRpcException } from "@/lib/rpcError.ts"
import { filterNotNull } from "@/lib/std.ts"

import type { AiAgentTaskContextItem, GitFile } from "@/lib/client/generated/TypeDefinitions.ts"

export function displayName(fullPath: string) {
  const pathSplit = fullPath.split("/").filter(it => it.length > 0)
  return pathSplit.length > 0 ? pathSplit[pathSplit.length - 1] : "<root>"
}

export function displayPath(fullPath: string) {
  const pathSplit = fullPath.split("/").filter(it => it.length > 0)
  if (pathSplit.length <= 1) {
    return "/"
  }

  return pathSplit.slice(0, pathSplit.length - 1).join("/") + "/"
}

export function shortRevisionId(id: string): string {
  return id.substring(0, 7)
}

const refsHeadsRegex = /^refs\/heads\//

export function branchDisplayName(branch: string) {
  return branch.replace(refsHeadsRegex, "")
}

export function getSortedFiles<T extends (GitFile | AiAgentTaskContextItem)>(files: T[] | undefined): T[] | undefined {
  return files?.slice().sort((a, b) => {
    if (a.type == "DIR" && b.type != "DIR") {
      return -1
    } else if (a.type != "DIR" && b.type == "DIR") {
      return 1
    } else {
      const pathA = "name" in a ? a.name : a.path
      const pathB = "name" in b ? b.name : b.path
      return pathA.localeCompare(pathB)
    }
  })
}

export function splitCommitMessage(message: string) {
  const [firstLine, ...remainingLines] = message.trim().split("\n")
  return [firstLine, remainingLines.filter(it => !it.startsWith("dm-checkpoint-id")).join("\n").trim()]
}

export function pageTitle(...parts: (string | null | undefined)[]) {
  return filterNotNull([...parts, ProductName]).join(" - ")
}

export const NEW_PROJECT_KEY = "new_matter_project"

export function projectDisplayName(name: string | undefined) {
  return name?.toLocaleLowerCase() === NEW_PROJECT_KEY.toLocaleLowerCase() ? "New project" : name
}

export const ProductName = "Matter by JetBrains"

const punctuationRegex = /[.,:;]+$/

export function formatTaskTitle(title: string | null): string | null {
  if (!title) {
    return title
  }

  return title.trim().replace(punctuationRegex, "")
}

export function pluralize(word: string, count: number) {
  if (count === 1) return word

  if (word.endsWith("s") || word.endsWith("sh") || word.endsWith("ch") || word.endsWith("x") || word.endsWith("z")) {
    return word + "es"
  }

  if (word.endsWith("y")) {
    if (word.endsWith("ay") || word.endsWith("ey") || word.endsWith("oy") || word.endsWith("uy")) {
      return word + "s"
    } else if (word.endsWith("iy")) {
      return word.slice(0, -1) + "es"
    } else {
      return word.slice(0, -1) + "ies"
    }
  }

  return word + "s"
}

export function formatDollars(value: number): string {
  // parseFloat removes any insignificant trailing zeros and the decimal point if not needed
  return parseFloat(value < 1.0 ? value.toFixed(3) : value.toFixed(2)).toLocaleString()
}

export function creditsToDollars(credits: number): number {
  return credits / 100_000
}

const rpcErrorRegex = /^RPC \S+\/\S+\s*/

export function formatErrorMessage(e: Error | unknown, defaultError: string): string {
  if (isRpcException(e)) {
    return extractRpcErrorMessage(e)
  } else if (e instanceof Error) {
    return e.message.replace(rpcErrorRegex, "")
  } else {
    return defaultError
  }
}

export const SECRET_VALUE_PLACEHOLDER = "••••••••••"